import { Spinner, Text } from "@ui-kitten/components";
import Popover from "components/Popover/Popover";
import { SeatDetails } from "features/types";
import React, { useMemo, useState } from "react";
import { Pressable, ScrollView, View } from "react-native";

const AvailableSeatDots: React.FC<{
  seatsByRow: Record<string, SeatDetails[]> | undefined;
  availableSeatIds: Set<number>;
  selectedSeats: SeatDetails[];
  primaryColor?: string;
  loading?: boolean;
  disabled?: boolean;
  onSelectionChange: (seats: SeatDetails[]) => void;
}> = ({
  seatsByRow,
  availableSeatIds,
  selectedSeats,
  primaryColor,
  loading,
  disabled,
  onSelectionChange,
}) => {
  const selectedSeatIds = useMemo(
    () => new Set(selectedSeats.map((s) => s.id)),
    [selectedSeats],
  );

  const [hoveredSeatId, setHoveredSeatId] = useState<number | null>(null);

  const rows = useMemo(() => {
    const entries = Object.entries(seatsByRow ?? {});
    entries.sort(([rowA], [rowB]) =>
      rowA.localeCompare(rowB, undefined, { numeric: true }),
    );

    return entries.map(([rowLabel, rowSeats]) => {
      const sortedSeats = [...rowSeats].sort((a, b) => {
        const seatA = a.seatDisplay || a.seat;
        const seatB = b.seatDisplay || b.seat;
        return seatA.localeCompare(seatB, undefined, { numeric: true });
      });
      return { rowLabel, seats: sortedSeats };
    });
  }, [seatsByRow]);

  const toggleSeat = (seat: SeatDetails) => {
    if (disabled) return;
    if (!availableSeatIds.has(seat.id)) return;
    if (selectedSeatIds.has(seat.id)) {
      onSelectionChange(selectedSeats.filter((s) => s.id !== seat.id));
      return;
    }
    onSelectionChange([...selectedSeats, seat]);
  };

  if (loading) {
    return (
      <View className="py-3">
        <Spinner />
      </View>
    );
  }

  if (!seatsByRow || Object.keys(seatsByRow).length === 0) {
    return (
      <View className="py-3">
        <Text appearance="hint">No available seats found.</Text>
      </View>
    );
  }

  return (
    <View className="mt-2">
      <Text category="s1">Select seats</Text>

      <View style={{ position: "relative" }}>
        <ScrollView className="mt-2">
          <View>
            <View className="items-center">
              <Text category="c2">Field</Text>
            </View>
            <View className="mt-5">
              {rows.map(({ rowLabel, seats }) => (
                <View key={rowLabel} className="flex-row justify-center">
                  {seats.map((seat) => {
                    const isSelected = selectedSeatIds.has(seat.id);
                    const isAvailable = availableSeatIds.has(seat.id);
                    const bg = isSelected
                      ? "#69b34c"
                      : isAvailable
                        ? primaryColor || "#3b82f6"
                        : "#ebebeb";

                    const seatLabel = seat.seatDisplay || seat.seat;

                    return (
                      <Pressable
                        key={seat.id}
                        disabled={disabled}
                        onPress={() => toggleSeat(seat)}
                        onHoverIn={() => {
                          if (!disabled) setHoveredSeatId(seat.id);
                        }}
                        onHoverOut={() => {
                          if (!disabled) setHoveredSeatId(null);
                        }}
                        style={disabled ? { opacity: 0.6 } : undefined}
                      >
                        <Popover
                          isOpen={hoveredSeatId === seat.id}
                          popoverText={`Row ${rowLabel} • Seat ${seatLabel}`}
                          positions={["top", "bottom"]}
                          onClickOutside={() => setHoveredSeatId(null)}
                        >
                          <View
                            style={{
                              margin: 1,
                              width: 12,
                              height: 12,
                              borderRadius: 999,
                              backgroundColor: bg,
                            }}
                          />
                        </Popover>
                      </Pressable>
                    );
                  })}
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

export default AvailableSeatDots;
